import React from 'react';
import { motion } from 'framer-motion';
import { 
  Download, Smartphone, CreditCard, BarChart3, Shield, 
  MessageSquare, Users, Globe, Zap, Building2, QrCode,
  CheckCircle2, ArrowRight, FileText, Bell, Clock, Receipt,
  Upload, Search, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const IMAGES = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/a36b95dc-b96e-44d1-a7dc-76d733fb2acd/images/43d56448b873a24878ac133d517863c400e102fad6292d95ac9ac592493e008e.png',
  mobile: 'https://static.prod-images.emergentagent.com/jobs/a36b95dc-b96e-44d1-a7dc-76d733fb2acd/images/203317a413ed1600f6a2e4948b51aabe31010a81543711dbf42b670192654fc7.png',
  admin: 'https://static.prod-images.emergentagent.com/jobs/a36b95dc-b96e-44d1-a7dc-76d733fb2acd/images/e3a8121a425281660213784aa123328aa8e0aee350b3c4f7cf58db43c5880abd.png',
  payments: 'https://static.prod-images.emergentagent.com/jobs/a36b95dc-b96e-44d1-a7dc-76d733fb2acd/images/653f7bf75eef167967ce7fa50d7310b76cba8cba7a76c873cb360a80259f7584.png',
};

const TEAL = '#0d9488';
const NAVY = '#1e3a5f';

const Section = ({ children, className = '', bg = 'white', id }) => (
  <section id={id} className={`py-16 md:py-20 px-6 sm:px-10 lg:px-16 ${className}`} style={{ backgroundColor: bg, pageBreakInside: 'avoid' }}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

const FeatureItem = ({ icon: Icon, title, desc, color = TEAL }) => (
  <div className="flex gap-4 items-start">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div>
      <h4 className="text-sm font-bold text-slate-900 mb-1">{title}</h4>
      <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
    </div>
  </div>
);

const ProcessStep = ({ num, title, desc, icon: Icon }) => (
  <div className="text-center" style={{ pageBreakInside: 'avoid' }}>
    <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white font-bold text-lg shadow-lg" style={{ backgroundColor: TEAL }}>
      <Icon className="w-6 h-6" />
    </div>
    <div className="w-8 h-8 rounded-full mx-auto -mt-6 mb-3 flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: NAVY }}>
      {num}
    </div>
    <h4 className="text-base font-bold text-slate-900 mb-1">{title}</h4>
    <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
  </div>
);

const Presentacion = () => {
  const handleDownloadPDF = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          section { page-break-inside: avoid; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-break { page-break-before: always; }
        }
      `}</style>

      {/* Floating Download Button */}
      <div className="fixed top-4 right-4 z-50 no-print">
        <Button onClick={handleDownloadPDF} size="lg" className="h-11 px-6 rounded-full bg-slate-900 text-white shadow-xl hover:bg-slate-800 font-semibold" data-testid="download-pdf-btn">
          <Download className="w-4 h-4 mr-2" /> Descargar PDF
        </Button>
      </div>

      {/* SLIDE 1: Cover */}
      <section className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0f2847 50%, #0a1f3d 100%)` }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full" style={{ backgroundColor: TEAL, filter: 'blur(100px)' }} />
          <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full" style={{ backgroundColor: TEAL, filter: 'blur(120px)' }} />
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center relative z-10 max-w-3xl">
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src="/macropago-logo.jpeg" alt="MacroPago" className="w-16 h-16 rounded-2xl shadow-lg" />
            <span className="text-4xl font-extrabold text-white tracking-tight">MacroPago</span>
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
            La billetera virtual que<br />
            <span style={{ color: TEAL }}>simplifica los cobros</span>
          </h1>
          <p className="text-lg text-blue-200/70 mb-8 max-w-xl mx-auto">
            Plataforma de gestion de cobranzas para empresas, reparticiones publicas, PYMES y emprendedores
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['Empresas', 'Reparticiones publicas', 'PYMES', 'Emprendedores'].map(t => (
              <span key={t} className="text-xs font-medium px-4 py-2 rounded-full border border-white/20 text-white/80">{t}</span>
            ))}
          </div>
          <img src={IMAGES.hero} alt="MacroPago Platform" className="mt-10 rounded-2xl shadow-2xl mx-auto max-w-lg w-full opacity-90" />
        </motion.div>
      </section>

      {/* SLIDE 2: El Problema */}
      <Section bg="#f8fafc" className="print-break">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: TEAL }}>El problema</p>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Cobrar no deberia ser complicado</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { title: 'Perseguir clientes', desc: 'Llamadas, mensajes, recorridos. Perdes tiempo y recursos buscando a cada cliente uno por uno.', icon: Clock },
            { title: 'Planillas y desorden', desc: 'Excel, papeles, cuentas en la cabeza. Sin sistema no hay control ni visibilidad del estado real.', icon: FileText },
            { title: 'Sin canales digitales', desc: 'Tus clientes quieren consultar y pagar desde el celular. Sin portal digital, perdes oportunidades de cobro.', icon: Smartphone },
          ].map((item) => (
            <div key={item.title} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-red-50">
                <item.icon className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* SLIDE 3: La Solucion */}
      <Section className="print-break">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: TEAL }}>La solucion</p>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">MacroPago: tu sistema de cobranzas completo</h2>
          <p className="text-base text-slate-500 max-w-2xl mx-auto">Una plataforma integral que conecta a tu empresa con tus clientes. Ellos consultan y pagan. Vos monitoreás y cobras.</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <img src={IMAGES.mobile} alt="Portal movil" className="rounded-2xl shadow-xl mx-auto max-w-xs w-full" />
          <div className="space-y-5">
            <FeatureItem icon={Globe} title="Portal de Autogestion" desc="Cada empresa tiene su portal personalizado donde los clientes consultan su deuda por DNI, CUIT o dominio." />
            <FeatureItem icon={CreditCard} title="Multiples medios de pago" desc="Mercado Pago (API o QR manual), transferencia bancaria, y mas. Cada empresa configura los suyos." />
            <FeatureItem icon={BarChart3} title="Dashboard de gestion" desc="Panel administrativo con metricas, listado de clientes, estados de pago y herramientas de gestion." />
            <FeatureItem icon={Building2} title="Multi-empresa" desc="Gestion centralizada de multiples empresas con branding, carteras y configuracion independiente." />
          </div>
        </div>
      </Section>

      {/* SLIDE 4: Proceso del Cliente */}
      <Section bg="#f8fafc" className="print-break">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: TEAL }}>Experiencia del cliente</p>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Asi funciona para tu cliente</h2>
          <p className="text-base text-slate-500">Simple, rapido y sin registros</p>
        </div>
        <div className="grid md:grid-cols-4 gap-6">
          <ProcessStep num={1} icon={Search} title="Busca su deuda" desc="Ingresa su DNI o dominio en el portal personalizado de tu empresa." />
          <ProcessStep num={2} icon={Eye} title="Ve el detalle" desc="Visualiza monto total, descuentos disponibles y fechas de vencimiento." />
          <ProcessStep num={3} icon={CreditCard} title="Elige como pagar" desc="Selecciona entre Mercado Pago, QR o transferencia bancaria." />
          <ProcessStep num={4} icon={CheckCircle2} title="Confirma el pago" desc="Paga al instante. La empresa recibe la notificacion automaticamente." />
        </div>
      </Section>

      {/* SLIDE 5: Panel Administrativo */}
      <Section className="print-break">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: TEAL }}>Panel administrativo</p>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Control total de tus cobranzas</h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-5">
            <FeatureItem icon={Upload} title="Carga masiva de clientes" desc="Importa miles de clientes desde Excel o CSV en segundos. Campos personalizados incluidos." />
            <FeatureItem icon={Users} title="Gestion de carteras" desc="Organiza clientes en carteras con configuracion de intereses y fechas de vencimiento." />
            <FeatureItem icon={BarChart3} title="Analytics en tiempo real" desc="Quienes consultaron su deuda, quienes pagaron, cuanto se recupero por periodo." />
            <FeatureItem icon={Bell} title="Campanas de WhatsApp" desc="Envio masivo de recordatorios por WhatsApp con metricas de entrega." />
            <FeatureItem icon={Shield} title="Equipo con roles" desc="Invita a tu equipo con permisos diferenciados: admin, supervisor, operador." />
          </div>
          <img src={IMAGES.admin} alt="Panel administrativo" className="rounded-2xl shadow-xl mx-auto max-w-lg w-full" />
        </div>
      </Section>

      {/* SLIDE 6: Metodos de Pago */}
      <Section bg="#f8fafc" className="print-break">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: TEAL }}>Metodos de pago</p>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Tu cliente elige como pagar</h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <img src={IMAGES.payments} alt="Metodos de pago" className="rounded-2xl shadow-xl mx-auto max-w-xs w-full" />
          <div className="space-y-4">
            {[
              { title: 'Mercado Pago (API)', desc: 'Integracion directa. El cliente paga con tarjeta, dinero en cuenta o cuotas.', color: '#0070f3' },
              { title: 'QR de Mercado Pago', desc: 'Modo manual con QR imprimible. Ideal para empresas sin integracion API.', color: '#00b4d8' },
              { title: 'Transferencia Bancaria', desc: 'CBU, alias y datos bancarios con boton de copia rapida. Logo del banco incluido.', color: '#059669' },
              { title: 'Pagos parciales', desc: 'El cliente selecciona las actas que quiere pagar. Flexibilidad total.', color: '#7c3aed' },
              { title: 'Descuentos con vencimiento', desc: 'Monto con quita y fecha limite. Incentiva el pago anticipado.', color: '#dc2626' },
            ].map((item) => (
              <div key={item.title} className="flex gap-3 items-start bg-white rounded-lg p-4 border border-slate-100">
                <div className="w-2 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                <div>
                  <h4 className="text-sm font-bold text-slate-900">{item.title}</h4>
                  <p className="text-xs text-slate-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* SLIDE 7: Modulo de Descargos */}
      <Section className="print-break">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#7c3aed' }}>Nuevo modulo</p>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Descargos digitales</h2>
          <p className="text-base text-slate-500 max-w-2xl mx-auto">Tus clientes pueden presentar descargos directamente desde el portal. Vos gestionas todo desde el panel.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-6">
            <h3 className="text-base font-bold text-violet-900 mb-4 flex items-center gap-2"><Smartphone className="w-5 h-5" /> Lo que ve el cliente</h3>
            <div className="space-y-3">
              {[
                'Selecciona la(s) acta(s) del descargo',
                'Elige la causa (configurables por el admin)',
                'Deja observaciones y adjunta archivos',
                'Ve el estado y la respuesta del admin',
              ].map((t) => (
                <div key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-violet-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-violet-800">{t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
            <h3 className="text-base font-bold text-indigo-900 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Lo que ve el admin</h3>
            <div className="space-y-3">
              {[
                'Panel con estadisticas: total, pendientes, aprobados, tasa',
                'Tabla filtrable por causa y estado',
                'Flujo: Pendiente → En revision → Aprobado/Rechazado',
                'Responde al cliente y descarga adjuntos',
                'Exporta todo a CSV o TXT',
              ].map((t) => (
                <div key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-indigo-800">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* SLIDE 8: Mas Funcionalidades */}
      <Section bg="#f8fafc" className="print-break">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: TEAL }}>Funcionalidades adicionales</p>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Todo lo que necesitas en un solo lugar</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: QrCode, title: 'Widget Web', desc: 'QR y boton embebible para tu sitio web. Tus clientes acceden al portal con un click.' },
            { icon: MessageSquare, title: 'Chat integrado', desc: 'Canal de comunicacion directo entre el cliente y tu equipo desde el portal.' },
            { icon: FileText, title: 'Solicitud de documentos', desc: 'El cliente pide actas o facturas. Tu equipo gestiona las solicitudes.' },
            { icon: Receipt, title: 'Reporte de pagos', desc: 'El cliente reporta su pago con comprobante. Verificacion desde el panel.' },
            { icon: Shield, title: 'Libre deuda', desc: 'Certificados de libre deuda automaticos cuando el cliente regulariza.' },
            { icon: Zap, title: 'Integraciones CRM', desc: 'Conecta con Moracero y otros sistemas para sincronizar datos automaticamente.' },
          ].map((f) => (
            <div key={f.title} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${TEAL}12` }}>
                <f.icon className="w-5 h-5" style={{ color: TEAL }} />
              </div>
              <h4 className="text-sm font-bold text-slate-900 mb-1">{f.title}</h4>
              <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* SLIDE 9: Personalizacion */}
      <Section className="print-break">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: TEAL }}>Personalizacion</p>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Tu marca, tu portal</h2>
          <p className="text-base text-slate-500 max-w-xl mx-auto">Cada empresa tiene su portal con branding propio</p>
        </div>
        <div className="grid md:grid-cols-4 gap-6 text-center">
          {[
            { title: 'Logo personalizado', desc: 'Tu logo en el portal y las comunicaciones' },
            { title: 'Colores de marca', desc: 'Color primario aplicado a todo el portal' },
            { title: 'URL propia', desc: 'macropago.com.ar/t/tu-empresa' },
            { title: 'Campos custom', desc: 'Agrega los campos que necesites a tus clientes' },
          ].map((item, i) => (
            <div key={item.title} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
              <div className="w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold" style={{ backgroundColor: TEAL }}>
                {i + 1}
              </div>
              <h4 className="text-sm font-bold text-slate-900 mb-1">{item.title}</h4>
              <p className="text-xs text-slate-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* SLIDE 10: CTA */}
      <section className="py-20 px-6 text-center print-break" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0a1f3d 100%)` }}>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src="/macropago-logo.jpeg" alt="MacroPago" className="w-12 h-12 rounded-xl" />
            <span className="text-2xl font-bold text-white">MacroPago</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Listo para simplificar tus cobros?</h2>
          <p className="text-base text-blue-200/70 mb-8 max-w-lg mx-auto">
            Unite a las empresas y organismos que ya cobran sin complicaciones.
          </p>
          <div className="no-print flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" onClick={() => window.location.href = '/login'} className="h-12 px-10 rounded-full bg-white text-slate-900 hover:bg-slate-100 font-bold text-base">
              Empezar ahora <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          <div className="mt-10 text-sm text-blue-200/50">
            <p>macropago.com.ar</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Presentacion;
